import { Injectable } from '@angular/core';
import * as speechSdk from "microsoft-cognitiveservices-speech-sdk";
import { Subject } from "rxjs";
import { Config, SpeechEvents } from "../../models/speech-to-text.interface";
import { MicrophoneErrorMessages } from 'app/util/constants';


@Injectable({
  providedIn: 'root'
})
export class SpeechToTextService {
  //Regex to find last . from sentense
  private findLastDot = /\.$/;

  //to control full stop of sentense while recognizing
  public removeLastFullStop = false;

  //to send regcognized text to outside of component
  recognizer: Subject<string> = new Subject();
  recognizing: Subject<string> = new Subject();

  speechEvent: Subject<SpeechEvents> = new Subject();

  //local variables
  speechConfig: speechSdk.SpeechConfig;
  audioConfig: speechSdk.AudioConfig;
  speechRecognizer: speechSdk.SpeechRecognizer;

  //local variable to check if full stop word triggered or not
  isFullStopCaptured: boolean = false;
  //configurations required to inti the object
  static configurations: Config;

  wakeLock = null;
  isWakeLockSupported = 'wakeLock' in navigator;


  constructor() {
    this.speechConfig = speechSdk.SpeechConfig.fromSubscription(SpeechToTextService.configurations.apiKey, SpeechToTextService.configurations.region);
    this.speechConfig.endpointId = SpeechToTextService.configurations.endpointId;
    this.speechConfig.speechRecognitionLanguage = SpeechToTextService.configurations.speechRecognitionLanguage;
    this.speechConfig.enableDictation();
    this.speechConfig.setProfanity(speechSdk.ProfanityOption.Raw);
    this.audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
    this.speechRecognizer = new speechSdk.SpeechRecognizer(
      this.speechConfig,
      this.audioConfig,
    );
    this.addEvents();
  }

  setSelectedInputDevice(deviceId: string) {
    if (this.speechRecognizer) {
      this.speechRecognizer.close();
    }
  
    this.audioConfig = speechSdk.AudioConfig.fromMicrophoneInput(deviceId);
    this.speechRecognizer = new speechSdk.SpeechRecognizer(this.speechConfig, this.audioConfig);
    this.addEvents();
  }


  //set initial configurations
  public static init(config: Config) {
    this.configurations = config;
  }


  //to add basic event for speech to text recognition
  addEvents() {
    this.recognizingSpeech();
    this.recognizedSpeech();
  }

  recognizingSpeech() {
    this.speechRecognizer.recognizing = (s, event) => {
      this.recognizing.next(event.result.text);
      this.processFullStop(event.result.text);
    };
  }

  recognizedSpeech() {
    this.speechRecognizer.recognized = (r, event) => {
      if (event.result.reason == speechSdk.ResultReason.RecognizedSpeech && event.result.text) {
        let recognizedText = event.result.text;
        if (this.removeLastFullStop) {
          recognizedText = this.removeFullStop(recognizedText);
        }
        this.recognizer.next(recognizedText);
      } else if (event.result.text == "" && this.isFullStopCaptured) {
        this.recognizer.next(".");
      }
    };
  }

  processFullStop(text: string): void {
    if (["full stop", "period"].indexOf(text) > -1) {
      this.isFullStopCaptured = true;
    } else {
      this.isFullStopCaptured = false;
    }
  }

  removeFullStop(text: string): string {
    return text.replace(this.findLastDot, "");
  }

  //to start the recording
  startRecording() {
    if (!this.isWakeLockSupported) {
      console.log('Screen Wake Lock is not supported in your browser.');
    }
    return new Promise((resolve, reject) => {
      this.speechRecognizer.startContinuousRecognitionAsync(
        async () => {
          if (this.isWakeLockSupported) {
            this.wakeLock = await navigator.wakeLock.request('screen');
            console.log('Screen Wake Lock acquired.');
          }
          resolve(true);
        },
        (err) => {
          reject(this.getErrorMessage(err));
        },
      );
    });
  }

  //to stop recording
  async stopRecording() {
    if (this.wakeLock) {
      await this.wakeLock.release();
      console.log('Screen Wake Lock released.');
      this.wakeLock = null;
    }
    return new Promise((resolve, reject) => {
      this.speechRecognizer.stopContinuousRecognitionAsync(() => {
        resolve(true);
      }, (err) => {
        reject(this.getErrorMessage(err));
      },
      );
    });
  }

  //one time use of speech to text
  recognizeOnce(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.speechRecognizer.recognizeOnceAsync(result => {
        if (result && result.text) {
          resolve(result.text);
        } else {
          reject(result)
        }
      });
    });
  }

  getErrorMessage(error: string): string {
    const errorName = error.split(':')[1]?.trim() || '';

    switch (errorName) {
      case 'NotAllowedError':
        return MicrophoneErrorMessages.NotAllowedError;
      case 'NotFoundError':
        return MicrophoneErrorMessages.NotFoundError;
      case 'NotReadableError':
        return MicrophoneErrorMessages.NotReadableError;
      case 'NotSupportedError':
        return MicrophoneErrorMessages.NotSupportedError;
      case 'SecurityError':
        return MicrophoneErrorMessages.SecurityError;
      case 'AbortError':
        return MicrophoneErrorMessages.AbortError;
      case 'OverconstrainedError':
        return MicrophoneErrorMessages.OverconstrainedError;
      case 'InvalidStateError':
        return MicrophoneErrorMessages.InvalidStateError;
      case 'TypeError':
        return MicrophoneErrorMessages.TypeError;
      default:
        return MicrophoneErrorMessages.UnknownError;
    }
  }

}
